import { lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";

import ErrorPage from "@/components/error-page";
import { Login } from "@/components/login";
import { ApiLogsPage } from "@/features/api-integration/pages/api-logs-page";
import { LenderUserRoleEnum } from "@/features/common/enums";
import { ApiPage } from "@/features/lender-companies/pages/api-page";
import { LenderProfilePage } from "@/features/lender-companies/pages/lender-profile";
import { SelectLenderCompanyPage } from "@/features/lender-companies/pages/select-lender-company-page";
import { TeamPage } from "@/features/lender-companies/pages/team-page";
import { LenderApplicationRedirectPage } from "@/features/page-layouts/lender-application-redirect-page";
import { LenderCompanyLayout } from "@/features/page-layouts/lender-company-layout";
import { RootPage } from "@/features/page-layouts/root-page";
import { MyProfilePage } from "@/features/users/pages/my-profile";

import AccountingDataPage from "./AccountingDataPage";
import Dashboard from "./Dashboard";
import Details from "./Details";
import { PrivateRoute } from "./PrivateRoute";
import SearchLenderApplications from "./SearchLenderApplications";

// eslint-disable-next-line react-refresh/only-export-components
const CallbackPage = lazy(() =>
  import("@krea/shared/features/callback-page").then((mod) => ({
    default: mod.CallbackPage,
  })),
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="callback-page/:callbackPageId" element={<CallbackPage />} />
      <Route path="/login" element={<Login />} />

      <Route path="/" element={<RootPage />} />
      <Route element={<PrivateRoute />}>
        <Route
          path="/select-lender-company"
          element={<SelectLenderCompanyPage />}
        />
        <Route
          path="/lender-application/:id"
          element={<LenderApplicationRedirectPage />}
        />
      </Route>

      <Route
        path="/lender-company/:lenderCompanyId"
        element={<LenderCompanyLayout />}
      >
        {/* Private Routes */}
        <Route element={<PrivateRoute />}>
          <Route
            index
            element={<Dashboard />}
            handle={{
              crumbs: () => [{ label: "Lender applications" }],
            }}
          />

          <Route
            path="search"
            element={<SearchLenderApplications />}
            handle={{
              crumbs: ({ params }: { params: { lenderCompanyId: string } }) => [
                {
                  label: "Lender applications",
                  to: `/lender-company/${params.lenderCompanyId}`,
                },
                { label: "Search" },
              ],
            }}
          />

          <Route
            path="lender-application/:id"
            element={<Details />}
            handle={{
              crumbs: ({
                params,
              }: {
                params: { lenderCompanyId: string; id: string };
              }) => [
                {
                  label: "Lender applications",
                  to: `/lender-company/${params.lenderCompanyId}`,
                },
                { label: params.id },
              ],
            }}
          />

          <Route
            path="lender-application/:id/accounting-data"
            element={<AccountingDataPage />}
            handle={{
              crumbs: ({
                params,
              }: {
                params: { lenderCompanyId: string; id: string };
              }) => [
                {
                  label: "Lender applications",
                  to: `/lender-company/${params.lenderCompanyId}`,
                },
                {
                  label: "Lender application",
                  to: `/lender-company/${params.lenderCompanyId}/lender-application/${params.id}`,
                },
                { label: "Accounting data" },
              ],
            }}
          />
          <Route
            path="profile"
            element={<MyProfilePage />}
            handle={{ crumbs: () => [{ label: "My Profile" }] }}
          />
        </Route>
        {/* Private Admin routes */}
        <Route
          element={
            <PrivateRoute rolesWithAccess={[LenderUserRoleEnum.ADMIN]} />
          }
        >
          {/* ✅ Make these RELATIVE (no leading /) */}
          <Route
            path="team"
            element={<TeamPage />}
            handle={{ crumbs: () => [{ label: "Team" }] }}
          />
          <Route
            path="company-profile"
            element={<LenderProfilePage />}
            handle={{ crumbs: () => [{ label: "Company Profile" }] }}
          />
        </Route>
        <Route
          element={
            <PrivateRoute
              rolesWithAccess={[
                LenderUserRoleEnum.ADMIN,
                LenderUserRoleEnum.DEVELOPER,
              ]}
            />
          }
        >
          <Route
            path="api"
            element={<ApiPage />}
            handle={{ crumbs: () => [{ label: "Api Configuration" }] }}
          />

          <Route
            path="api-logs"
            element={<ApiLogsPage />}
            handle={{ crumbs: () => [{ label: "Api logs" }] }}
          />
        </Route>
      </Route>

      <Route element={<ErrorPage is404error />} />
    </>,
  ),
);
